import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { AlertTriangle } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Fact, Facts } from 'shared/components/facts'
import { SectionTitle } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import { Alert, AlertTitle } from 'shared/components/ui/alert'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shared/components/ui/dialog'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'
import { ShiftCloseForm } from './ShiftCloseForm'
import { MONEY_COLUMN, som, sum, WHOLE } from 'shared/lib/money'

type Collection = RouterOutputs['shifts']['Current']['collections'][number]

const closeShift = (input: Parameters<typeof trpcClient.shifts.Close.mutate>[0]) =>
	trpcClient.shifts.Close.mutate(input)

const closeOnBehalf =
	(pointId: string) => (input: Parameters<typeof trpcClient.shifts.Close.mutate>[0]) =>
		trpcClient.shifts.CloseOnBehalf.mutate({ pointId, ...input })

type LastClose = { difference: number; onBehalf: boolean }

/**
 * The Point's Shift (ADR-0035): one open Shift of one operator at a time. With
 * none open, the operator opens theirs; with theirs open, they see the cash taken
 * in it and close it with the counted drawer, in a dialog; with another operator's
 * open, they see whose it is — that operator closes it first. An admin watches, and
 * closes the open Shift on behalf of an operator who left without closing it
 * (ticket 14): the difference is still recorded against that operator.
 *
 * `toolbar` is the row the page's main button sits in, at its right: the Point
 * field is on its left.
 */
export function ShiftCard({
	locationId,
	isOperator,
	isAdmin = false,
	toolbar,
}: {
	locationId?: string
	isOperator: boolean
	isAdmin?: boolean
	toolbar: (action: ReactNode) => ReactNode
}) {
	const { t } = useTranslate()
	const queryClient = useQueryClient()
	const currentQuery = useQuery(trpc.shifts.Current.queryOptions({ pointId: locationId }))
	const view = currentQuery.data
	const shift = view?.shift ?? null
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const [closing, setClosing] = useState(false)
	const [lastClose, setLastClose] = useState<LastClose | null>(null)

	const refresh = () => {
		void currentQuery.refetch()
		void queryClient.invalidateQueries({ queryKey: trpc.cashbox.Cashbox.queryKey() })
	}

	const open = async () => {
		setBusy(true)
		setMessage(null)
		try {
			const res = await trpcClient.shifts.Open.mutate({})
			if (res.outcome === 'another_shift_open') {
				setMessage(t('shift.anotherOpen', { name: res.shift.operatorName }))
			} else {
				setLastClose(null)
			}
			refresh()
		} catch {
			setMessage(t('shift.openFailed'))
		} finally {
			setBusy(false)
		}
	}

	const onClosed = (onBehalf: boolean) => (closed: { difference: number | null }) => {
		setClosing(false)
		setLastClose({ difference: closed.difference ?? 0, onBehalf })
		refresh()
	}

	// The page's one main button: open the Shift, close one's own, or — the admin — close it for the operator.
	const mine = isOperator && !!view?.mine
	const onBehalf = isAdmin && !!locationId
	const action = !view ? null : !shift ? (
		isOperator && (
			<Button data-testid='shift-open' disabled={busy} onClick={open}>
				{t('shift.open')}
			</Button>
		)
	) : mine || onBehalf ? (
		<Button
			variant={mine ? 'default' : 'outline'}
			data-testid={mine ? 'shift-close-open' : 'shift-close-on-behalf-open'}
			onClick={() => setClosing(true)}
		>
			{t(mine ? 'shift.close' : 'shift.closeOnBehalfTitle')}
		</Button>
	) : null

	return (
		<div className='flex flex-col gap-6' data-testid='shift-card'>
			{toolbar(action)}

			{message && (
				<Alert variant='destructive' data-testid='shift-open-message'>
					<AlertTriangle />
					<AlertTitle>{message}</AlertTitle>
				</Alert>
			)}
			{lastClose && <ClosedResult close={lastClose} />}

			{currentQuery.isError ? (
				<p role='alert' className='text-sm text-destructive'>
					{t('shift.loadFailed')}
				</p>
			) : !view ? (
				<p className='text-sm text-muted-foreground'>{t('shift.loading')}</p>
			) : !shift ? (
				<p className='text-sm text-muted-foreground' data-testid='shift-none'>
					{t('shift.none')}
				</p>
			) : (
				<>
					<div className='flex flex-col gap-3'>
						<Facts testId='shift-open-info'>
							<Fact label={t('shift.operator')}>{shift.operatorName}</Fact>
							<Fact label={t('shift.openedAt')}>{formatDayTime(shift.openedAt)}</Fact>
							<Fact label={t('shift.taken')} testId='shift-taken'>
								<span className='tabular-nums'>
									{sum(view.collections.filter((c) => !c.cancelled).reduce((n, c) => n + c.amount, 0))}
								</span>
							</Fact>
						</Facts>
						{isOperator && !view.mine && (
							<p className='text-sm text-muted-foreground' data-testid='shift-not-mine'>
								{t('shift.notMine')}
							</p>
						)}
					</div>

					<section className='flex flex-col gap-2'>
						<SectionTitle title={t('shift.collectionsTitle')} count={view.collections.length} />
						<Collections rows={view.collections} />
					</section>
				</>
			)}

			<Dialog open={closing} onOpenChange={setClosing}>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<DialogTitle>{t(onBehalf ? 'shift.closeOnBehalfTitle' : 'shift.closeTitle')}</DialogTitle>
						<DialogDescription>
							{t(onBehalf ? 'shift.closeOnBehalfExplain' : 'shift.closeExplain', {
								name: shift?.operatorName ?? '',
							})}
						</DialogDescription>
					</DialogHeader>
					{onBehalf && locationId ? (
						<ShiftCloseForm
							onBehalf
							close={closeOnBehalf(locationId)}
							onCancel={() => setClosing(false)}
							onClosed={onClosed(true)}
						/>
					) : (
						<ShiftCloseForm close={closeShift} onCancel={() => setClosing(false)} onClosed={onClosed(false)} />
					)}
				</DialogContent>
			</Dialog>
		</div>
	)
}

/** What the close did: the drawer matched, or the difference and whom it is against. */
function ClosedResult({ close }: { close: LastClose }) {
	const { t } = useTranslate()
	const amount = som(t, Math.abs(close.difference))
	const detail = close.onBehalf
		? close.difference === 0
			? t('shift.closedOnBehalfExact')
			: t(close.difference < 0 ? 'shift.closedOnBehalfShortage' : 'shift.closedOnBehalfSurplus', { amount })
		: close.difference === 0
			? t('shift.closedExact')
			: t(close.difference < 0 ? 'shift.closedShortage' : 'shift.closedSurplus', { amount })
	return (
		<ToneNote
			tone={close.difference === 0 ? 'ready' : 'attention'}
			title={t('shift.closedTitle')}
			detail={detail}
			testId='shift-closed-result'
		/>
	)
}

/** The cash taken in the Shift: each by its Parcel's tracking code; a cancelled one stays, marked. */
function Collections({ rows }: { rows: Collection[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<Collection>[]>(
		() => [
			{
				id: 'parcel',
				header: t('money.colParcel'),
				cell: ({ row: { original: c } }) => (
					<span className='flex items-center gap-2'>
						<span className='font-mono' data-testid={`shift-row-code-${c.parcelId}`}>
							{c.trackingCode || c.parcelId}
						</span>
						{c.cancelled && (
							<Badge variant='outline' className='font-normal'>
								{t('shift.cancelled')}
							</Badge>
						)}
					</span>
				),
			},
			{
				id: 'at',
				header: t('money.colAt'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.at)}</span>,
			},
			{
				id: 'amount',
				header: t('money.colAmount'),
				meta: MONEY_COLUMN,
				cell: ({ row: { original: c } }) => (
					<span className={cn(c.cancelled && 'text-muted-foreground')}>{sum(c.amount)}</span>
				),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		getRowId: (c, i) => `${c.parcelId}-${i}`,
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			emptyText={t('shift.collectionsEmpty')}
			row={{ attributes: (c) => ({ 'data-testid': `shift-row-${c.parcelId}`, 'data-cancelled': c.cancelled }) }}
		/>
	)
}
